import type {
  Data,
  Part,
  PartExpand,
  PartSchema,
  Schema,
  SeparatedParts,
} from 'schemaDefinition/types';
import {evaluateCondition, isData, isNullish} from 'schemaDefinition/functions';
import type {
  ValidateSingleTypeValue,
  ValidationArgs,
  ValidationResult,
} from './types';
import {assertData} from './assertData';
import {assertDataValidationArgs} from './assertDataValidationArgs';
import {fail} from './fail';
import {getValue} from './getValue';
import {validResult} from './validResult';
import {validateValuePart} from './validateValuePart';
import {validateValueType} from './validateValueType';
import {warning} from './warning';

/**
 * validates data according to schema
 */
export const validateSchema = (
  schema: Schema,
  args: ValidationArgs,
  aggregateResult: ValidationResult,
): ValidationResult => {
  if (!isData(args.value)) {
    return fail(
      {
        ...args,
        part: schema,
        validation: 'invalid_type',
        messageKey: 'invalid.type',
      },
      aggregateResult,
    );
  }

  return validateParts(schema.parts, args, aggregateResult);
};

/**
 * validates data according to given parts, i.e. from a schema or expand case/default
 */
const validateParts = (
  parts: SeparatedParts,
  args: ValidationArgs,
  aggregateResult: ValidationResult,
): ValidationResult => {
  return parts.reduce(
    (acc, part) =>
      Array.isArray(part)
        ? validateParts(part, args, acc)
        : part.type === 'separator' || part.type === 'action'
          ? acc
          : validatePart(part, args, acc),
    aggregateResult,
  );
};

/**
 * validates data according part, i.e.:
 * - expand part: validate data according to any active case
 * - schema part: validate data[part.name] according to subschema
 * - "value" parts: validate data[part.name] according to part
 */
export const validatePart = (
  part: Part,
  args: ValidationArgs,
  aggregateResult: ValidationResult = validResult(),
): ValidationResult => {
  switch (part.type) {
    case 'schema': {
      return validateSchemaPart(
        part,
        assertDataValidationArgs(args),
        aggregateResult,
      );
    }
    case 'expand': {
      return validateExpandPart(
        part,
        assertDataValidationArgs(args),
        aggregateResult,
      );
    }
    default: {
      return validateValuePart(
        part,
        assertDataValidationArgs(args),
        aggregateResult,
      );
    }
  }
};

/**
 * validates data to any active case
 */
const validateExpandPart = (
  part: PartExpand,
  args: ValidationArgs<Data>,
  aggregateResult: ValidationResult,
): ValidationResult => {
  // Determine active case, if any
  const activeCase = part.when.find(w => evaluateCondition(w.condition, args));

  const activeParts = activeCase ? activeCase.parts : part.default;
  return activeParts
    ? validateParts(activeParts, args, aggregateResult)
    : aggregateResult;
};

/**
 * validates data[part.name] according to "value" part
 */
export const validateSchemaPart = (
  part: PartSchema,
  args: ValidationArgs<Data>,
  aggregateResult: ValidationResult = validResult(),
): ValidationResult => {
  if (part.noValidation) {
    return aggregateResult;
  }

  const {value, valuePath} = getValue(args.value, part, args.valuePath);

  return validateValueType(
    part,
    validateSingleSchemaInstance,
    {
      ...args,
      value,
      valuePath: valuePath,
      // Might want to keep the current localscope.
      localScope: undefined,
    },
    aggregateResult,
  );
};

const validateSingleSchemaInstance: ValidateSingleTypeValue<PartSchema> = (
  part: PartSchema,
  args,
  aggregateResult: ValidationResult = validResult(),
): ValidationResult => {
  if (!part.required && isNullish(args.value)) {
    return aggregateResult;
  }

  if (part.required && !args.value) {
    return (part.required === true ? fail : warning)(
      {
        ...args,
        part,
        validation: 'required',
        messageKey: 'required',
      },
      aggregateResult,
    );
  }

  return validateSchema(
    part,
    {
      ...args,
      localScope: assertData(args.value),
    },
    aggregateResult,
  );
};
